/**
 * WidgetRenderer — dispatch d'un Widget vers son composant de rendu, avec
 * isolation d'erreur par widget (Ph2a T9).
 *
 * Porté depuis l'esprit de Metrix (`frontend/src/features/reports/WidgetRenderer.tsx`),
 * réduit au strict dispatch pour les 6 widgets "cœur" de cette phase : la
 * récupération de données est gérée en amont (voir `useWidgetData.ts`), ce
 * composant se contente de choisir le bon widget et de passer ses props.
 *
 * Un `try/catch` classique ne capture pas les erreurs survenant pendant le
 * rendu React (elles se produisent de façon asynchrone dans le reconciler,
 * hors de la pile d'appel du composant parent). La seule façon de les
 * intercepter est une ErrorBoundary basée sur une classe React implémentant
 * `static getDerivedStateFromError` / `componentDidCatch`. On l'utilise ici
 * pour isoler chaque widget : si l'un d'eux jette pendant le rendu, seul son
 * emplacement affiche un fallback, le reste du rapport continue de s'afficher.
 */
import { Component, type ErrorInfo, type ReactNode } from 'react';
import type { Widget } from './schemas';
import WidgetShell from './WidgetShell';
import MetricCardWidget from './widgets/MetricCardWidget';
import LineChartWidget from './widgets/LineChartWidget';
import BarChartWidget from './widgets/BarChartWidget';
import TableWidget from './widgets/TableWidget';
import TopListWidget from './widgets/TopListWidget';
import DividerWidget from './widgets/DividerWidget';
import AreaChartWidget from './widgets/AreaChartWidget';
import DonutPieWidget from './widgets/DonutPieWidget';
import GaugeWidget from './widgets/GaugeWidget';
import ProgressBarWidget from './widgets/ProgressBarWidget';
import ComparisonMetricWidget from './widgets/ComparisonMetricWidget';
import TreemapWidget from './widgets/TreemapWidget';
import MapWidget from './widgets/MapWidget';
import CalendarHeatmapWidget from './widgets/CalendarHeatmapWidget';
import ImageBlockWidget from './widgets/ImageBlockWidget';
import RichTextBlockWidget from './widgets/RichTextBlockWidget';
import DateRangeDisplayWidget, { type EffectivePeriod } from './widgets/DateRangeDisplayWidget';
import type { MapView } from './widgets/mapLayers';

type Row = Record<string, string | number>;

export interface WidgetRendererProps {
  widget: Widget;
  data: { current: Row[]; previous: Row[] | null; meta: any } | null;
  loading?: boolean;
  error?: string;
  /**
   * Période effective du rapport (celle du `PeriodPicker`). Les widgets de données
   * la reçoivent déjà via `meta` (le backend la renvoie), mais les blocs de mise en
   * page n'interrogent rien : sans cette prop, `date_range_display` afficherait
   * « Aucune période » en mode `inherit`.
   */
  period?: EffectivePeriod | null;
  /**
   * Mode édition du builder. Seul `rich_text` l'exploite pour l'instant : il monte
   * alors `TipTapEditor` au lieu du rendu figé.
   */
  editable?: boolean;
  /** Remontée du HTML édité (persisté dans `config.display.content_html` par le parent). */
  onContentChange?: (html: string) => void;
  /** Remontée de la vue figée capturée sur `map` (`null` = « Réinitialiser »). */
  onViewChange?: (view: MapView | null) => void;
}

/**
 * Padding porté par WidgetShell (et non plus par chaque widget) : c'est ce qui fait
 * que le badge/l'icône du cadre stylé sont dans la même boîte paddée que le contenu
 * (plus de badge collé au coin). Une valeur par type ; `image_block` = aucun padding
 * (l'image épouse les bords). Les charts gardent leur padding serré `p-2`.
 */
const PAD: Partial<Record<Widget['type'], string>> = {
  metric_card: 'p-5',
  comparison_metric: 'p-5',
  progress_bar: 'p-5',
  gauge: 'p-2',
  top_list: 'p-4',
  table: 'p-4',
  rich_text: 'p-4',
  date_range_display: 'p-3',
  image_block: '',
  line_chart: 'p-2',
  bar_chart: 'p-2',
  area_chart: 'p-2',
  donut_pie: 'p-2',
  treemap: 'p-2',
  map: 'p-2',
  calendar_heatmap: 'p-2',
};

interface WidgetErrorBoundaryState {
  hasError: boolean;
  message?: string;
}

/** Isole les erreurs de rendu d'un widget pour ne pas casser le reste de la page. */
class WidgetErrorBoundary extends Component<{ children: ReactNode }, WidgetErrorBoundaryState> {
  state: WidgetErrorBoundaryState = { hasError: false };

  static getDerivedStateFromError(err: unknown): WidgetErrorBoundaryState {
    return { hasError: true, message: err instanceof Error ? err.message : String(err) };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // Log console uniquement — pas de reporting externe dans ce périmètre.
    console.error('[WidgetRenderer] Erreur de rendu widget', error, info.componentStack);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="rounded-xl border border-[#E2E4ED] bg-white p-5 h-full flex flex-col items-center justify-center gap-1 text-center">
          <span className="text-sm font-medium text-red-600">Widget en erreur</span>
          {this.state.message && (
            <span className="text-xs text-red-600/80 break-words">{this.state.message}</span>
          )}
        </div>
      );
    }
    return this.props.children;
  }
}

function UnsupportedWidget() {
  return (
    <div className="rounded-xl border border-[#E2E4ED] bg-white p-5 h-full flex items-center justify-center">
      <span className="text-sm text-[#8B8FA7]">Type de widget non supporté</span>
    </div>
  );
}

export default function WidgetRenderer({
  widget,
  data,
  loading,
  error,
  period,
  editable,
  onContentChange,
  onViewChange,
}: WidgetRendererProps) {
  function renderWidget() {
    // Props communes : tous les widgets partagent la même signature de base.
    const common = { config: widget.config, data, loading, error };

    switch (widget.type) {
      case 'metric_card':
        return <MetricCardWidget {...common} />;
      case 'line_chart':
        return <LineChartWidget {...common} />;
      case 'bar_chart':
        return <BarChartWidget {...common} />;
      case 'table':
        return <TableWidget {...common} />;
      case 'top_list':
        return <TopListWidget {...common} />;
      case 'divider':
        return <DividerWidget {...common} />;
      case 'area_chart':
        return <AreaChartWidget {...common} />;
      case 'donut_pie':
        return <DonutPieWidget {...common} />;
      case 'gauge':
        return <GaugeWidget {...common} />;
      case 'progress_bar':
        return <ProgressBarWidget {...common} />;
      case 'comparison_metric':
        return <ComparisonMetricWidget {...common} />;
      case 'treemap':
        return <TreemapWidget {...common} />;
      case 'map':
        return <MapWidget {...common} editable={editable} onViewChange={onViewChange} />;
      case 'calendar_heatmap':
        return <CalendarHeatmapWidget {...common} />;
      case 'image_block':
        return <ImageBlockWidget {...common} />;
      case 'rich_text':
        // Seul widget éditable au clic dans le canvas (TipTap inline).
        return <RichTextBlockWidget {...common} editable={editable} onChange={onContentChange} />;
      case 'date_range_display':
        // Seul widget à avoir besoin de la période du rapport : il n'interroge rien.
        return <DateRangeDisplayWidget {...common} period={period} />;
      default:
        return <UnsupportedWidget />;
    }
  }

  // Le cadre stylable partagé (fond / bordure / ombre / badge pilotés par `display.*`)
  // enveloppe chaque widget, charts inclus. Seul le `divider` reste nu. Le padding + `h-full`
  // (table PAD) fait porter au shell ce que le cadre local de chaque widget assurait avant.
  const bare = widget.type === 'divider';
  const content = bare ? (
    renderWidget()
  ) : (
    <WidgetShell display={widget.config.display} className={`${PAD[widget.type] ?? 'p-4'} h-full`}>
      {renderWidget()}
    </WidgetShell>
  );

  return <WidgetErrorBoundary>{content}</WidgetErrorBoundary>;
}
